import { useState } from 'react'
import { hashKey, skipToken, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '@/redux/hooks'
import { returnsApi } from '@/redux/api/returns-api-slice'
import { CalculateRequest } from '@itsrever/returns-api-types'

export const useSummaryData = () => {
  const { i18n } = useTranslation()

  const ecommerceId = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_id
  )

  const checkoutId = useAppSelector(
    (store) => store.returnsApi.order.response.checkout_id
  )

  const returnState = useAppSelector((store) => store.return)

  return {
    ecommerceID: ecommerceId,
    calculateData: {
      checkout_id: checkoutId,
      items_to_return: returnState.returnedItems.map((item) => item.item),
      exchange_selections: returnState.newItems.map((item) => ({
        line_item_platform_id: item.returnedItemId,
        quantity: item.quantity,
        variant_id: item.productVariant.variantId,
        signed_product_raw: item.signedProductRaw,
        collection_ids: item.collectionIds
      })),
      refund_method: returnState.refundPaymentMethod.method,
      return_method: returnState.returnMethod,
      pickup_address: returnState.returnAddress,
      pickup_ranges: returnState.pickUpRanges,
      drop_off_address: returnState.returnAddress,
      user_selected_carrier: returnState.userSelectedCarrier,
      user_selected_provider: returnState.userSelectedProvider,
      exchange_shipping_address: returnState.exchangeAddress,
      keep_your_item_selected: returnState.keepYourItem,
      user_preferred_lang: i18n.language
    }
  }
}

export const useSummary = (overrides?: Partial<CalculateRequest>) => {
  const storedSummary = useAppSelector(
    (store) => store.returnsApi.calculate.response
  )

  const {
    ecommerceID,
    calculateData: { checkout_id, ...calculateData }
  } = useSummaryData()

  const hasItemsToReturn = calculateData.items_to_return.length > 0

  const queryKey = [
    'summary',
    ecommerceID,
    checkout_id,
    calculateData,
    overrides
  ]

  const [initialKeyHash] = useState(() => hashKey(queryKey))
  const matchesInitialKey = hashKey(queryKey) === initialKeyHash

  return useQuery({
    queryKey,
    queryFn:
      ecommerceID && checkout_id && hasItemsToReturn
        ? async () => {
            const { data } = await returnsApi.calculate({
              ecommerceID,
              calculateData: {
                ...calculateData,
                checkout_id: checkout_id,
                ...overrides
              }
            })

            return data
          }
        : skipToken,
    initialData: matchesInitialKey ? storedSummary : undefined,
    placeholderData: (data) => data,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false
  })
}
